import { useState, useEffect } from 'react'
import Modal from 'aldoo-ra/Modal'
import ContentEditor from 'aldoo-ra/CMS/content-editor'
import { showNotification } from 'aldoo-ra/Notification'
import { SaveCMSContent, GetCMSContent } from 'aldoo-ra/CMS/api'
import TypeResolver from 'aldoo-ra/CMS/type-resolver'
import { getPreSaveFormatter } from 'aldoo-ra/CMS/content-pre-save-formatter'

const AldooComponentDialog = ({ isOpen, onClose, component }) => {
  const { getContentType } = TypeResolver()
  const AldooComponentType = getContentType('AldooComponent')

  const [selectedInstance, setSelectedInstance] = useState(null)

  useEffect(() => {
    if (component) {
      // Editing existing component - flatten content fields for ContentEditor
      const content = { ...component.content }

      // Clean up corrupted nested _id if it exists in content
      if (content._id && typeof content._id === 'object') {
        delete content._id
      }

      setSelectedInstance({
        _id: component._id,
        ...content
      })
    } else {
      // Creating new component
      setSelectedInstance(null)
    }
  }, [component, isOpen])

  const handleSave = async (fieldValues) => {
    // Check if a component with this id already exists (only for new components)
    if (!component?._id && fieldValues.id) {
      const existingResponse = await GetCMSContent({
        match: {
          'content.type': 'AldooComponent',
          'content.id': fieldValues.id.trim()
        },
        itemsPerPage: 1
      })

      if (existingResponse.result && existingResponse.data && existingResponse.data.length > 0) {
        showNotification({
          message: `A component with ID "${fieldValues.id}" already exists`,
          className: 'bg-admin_error text-white'
        })
        return null
      }
    }

    // ContentEditor passes the field values, we need to wrap them in content object
    let contentData = {
      ...fieldValues,
      type: 'AldooComponent'
    }

    // Apply pre-save formatter to normalize data (e.g., category field)
    const preFormatter = getPreSaveFormatter('AldooComponent')
    if (preFormatter) {
      contentData = preFormatter(contentData)
    }

    const savePayload = {
      content: contentData
    }

    // If editing, include the _id at root level
    if (component?._id) {
      savePayload._id = component._id
    }

    const response = await SaveCMSContent({ payload: savePayload })

    if (response.result) {
      showNotification({
        message: `Component "${fieldValues.name || fieldValues.id}" ${component ? 'updated' : 'created'} successfully`,
        className: 'bg-admin_primary text-admin_text_inv'
      })
      onClose()
      return response
    } else{
      showNotification({
        message: response.error || 'Failed to save component',
        className: 'bg-admin_error text-white'
      })
      return null
    }
  }

  const handleClose = () => {
    setSelectedInstance(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      variant="admin"
      className="w-full md:w-[90%] xl:w-[1200px]"
      zIndex={200}
    >
      <ContentEditor
        contentType={AldooComponentType}
        fieldValues={selectedInstance || {}}
        onSave={handleSave}
        onClickSave={(result) => {
          // Only close if save was successful
          if (!result) return
          handleClose()
        }}
        onCancel={handleClose}
      />
    </Modal>
  )
}

export default AldooComponentDialog
